import React from 'react';
import { Monitor, Smartphone, Tablet } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PLATFORM_IMG =
  'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1000&q=80';

const Features = () => {
  const { t } = useLanguage();

  return (
    <section id="platform" className="py-24 bg-section-teal">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div
            className="relative order-2 lg:order-1 overflow-hidden shadow-float ring-1 ring-line min-h-[360px]"
            style={{ borderRadius: '1.75rem' }}
          >
            <img
              src={PLATFORM_IMG}
              alt={t.reasons.panelAlt}
              className="absolute inset-0 w-full h-full object-cover"
              width={1000}
              height={700}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-teal/20 to-transparent" />
            <div
              className="absolute bottom-6 left-6 right-6 bg-surface/95 backdrop-blur border border-line p-5"
              style={{ borderRadius: '14px' }}
            >
              <p className="text-ink font-semibold text-sm">{t.platform.devicesTitle}</p>
              <p className="text-muted text-xs mt-1 mb-4">{t.platform.devicesSub}</p>
              <div className="flex gap-4 text-teal">
                <Monitor className="w-5 h-5" aria-hidden />
                <Tablet className="w-5 h-5" aria-hidden />
                <Smartphone className="w-5 h-5" aria-hidden />
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 space-y-6">
            <span className="text-coral text-sm font-bold uppercase tracking-[0.18em]">{t.platform.kicker}</span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-ink font-heading leading-tight text-balance">
              {t.platform.title}{' '}
              <span className="text-teal">{t.platform.titleAccent}</span>
            </h2>
            <p className="text-lg text-muted leading-relaxed">{t.platform.body}</p>
            <blockquote className="border-l-4 border-coral pl-5 font-accent italic text-ink text-lg leading-relaxed">
              {t.platform.highlight}
            </blockquote>
          </div>
        </div>

        <div id="capabilities" className="mt-28">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <span className="text-teal text-sm font-bold uppercase tracking-[0.18em]">{t.capabilities.kicker}</span>
              <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold text-ink font-heading">
                {t.capabilities.title}{' '}
                <span className="text-coral">{t.capabilities.titleAccent}</span>
              </h2>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.capabilities.items.map((item, index) => (
              <article
                key={item.title}
                className={`group bg-surface border border-line p-7 hover:border-teal/30 hover:shadow-card transition-all duration-300 ${
                  index === 0 ? 'lg:row-span-1 lg:bg-teal text-white lg:border-teal' : ''
                }`}
                style={{ borderRadius: '1.25rem' }}
              >
                <span
                  className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 mb-4 ${
                    index === 0
                      ? 'bg-white/15 text-white/90'
                      : 'bg-sand text-teal'
                  }`}
                  style={{ borderRadius: '6px' }}
                >
                  {item.tag}
                </span>
                <h3
                  className={`text-lg font-bold mb-2 font-heading ${
                    index === 0 ? 'text-white' : 'text-ink'
                  }`}
                >
                  {item.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${
                    index === 0 ? 'text-white/85' : 'text-muted'
                  }`}
                >
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
